import { atom, react, type Atom, type Signal } from "@tldraw/state";
import {
  squashRecordDiffs,
  type RecordsDiff,
  type StoreSchema,
} from "@tldraw/store";
import {
  createPresenceStateDerivation,
  type TLRecord,
  type TLStore,
  type TLUser,
} from "@tldraw/tlschema";
import { useEffect, useState } from "react";
import { applyRecordOp, diffFields } from "../../shared/sync/diff";
import { mergeFields } from "../../shared/record-fields";
import { isEqual } from "@tldraw/utils";
import { reconcileStoreHistory } from "./engine/history";
import {
  SYNC_PROTOCOL,
  type RecordsDiffMessage,
  type ServerMessage,
} from "../../shared/sync/protocol";

// Keeps a local record store in sync with the board room. Local edits apply
// immediately and are pushed as field-level changes; when the server reports
// other changes, unacknowledged local edits are replayed on top of them.

export type Collaboration = {
  status: Atom<"offline" | "online">;
  mode: Atom<"readonly" | "readwrite">;
};
export type BoardSyncState =
  | { status: "loading"; store?: undefined; error?: undefined }
  | { status: "error"; error: Error; store?: undefined }
  | {
      status: "synced-remote";
      connectionStatus: "offline" | "online";
      store: TLStore;
      error?: undefined;
    };
export type BoardSyncOptions = {
  uri: string;
  schema: StoreSchema<TLRecord, any>;
  /** Builds the editor's store; the engine supplies its own store options. */
  createStore(collaboration: Collaboration): TLStore;
  user?: Signal<TLUser | null>;
  onCustomMessage?(data: unknown): void;
};

type Pending = { clientClock: number; ops: RecordsDiffMessage };
const PRESENCE_MS = 50;
const PUSH_BYTES = 512 * 1024;

const activeClients = new Set<BoardSyncClient>();

/**
 * Resolve once this tab's edits have reached the server (or after `timeout`).
 * Server-side work that reads the board, such as an agent run started right
 * after an edit, should wait for this so it sees that edit.
 */
export async function whenEditsSaved(timeout = 5000) {
  const until = Date.now() + timeout;
  // The store reports local edits on the next animation frame; wait two so any
  // edit made just now has been queued as a push before checking.
  const frame = () =>
    new Promise<void>((resolve) => {
      const fallback = setTimeout(resolve, 250);
      requestAnimationFrame(() => {
        clearTimeout(fallback);
        resolve();
      });
    });
  await frame();
  await frame();
  while (Date.now() < until && [...activeClients].some((c) => !c.isSettled()))
    await new Promise((resolve) => setTimeout(resolve, 50));
}

export class BoardSyncClient {
  readonly sessionId = crypto.randomUUID();
  private store?: TLStore;
  private readonly collaboration: Collaboration = {
    status: atom("sync status", "offline"),
    mode: atom("sync mode", "readwrite"),
  };
  private ws: WebSocket | null = null;
  private serverClock = -1;
  private clientClock = 0;
  private pending: Pending[] = [];
  private outgoing: RecordsDiff<TLRecord> | null = null;
  /** Last server state of every record with unacknowledged local edits. */
  private readonly server = new Map<string, TLRecord | null>();
  private retry = 0;
  private timer?: ReturnType<typeof setTimeout>;
  private connectTimer?: ReturnType<typeof setTimeout>;
  private presenceTimer?: ReturnType<typeof setTimeout>;
  private disposed = false;
  private stops: Array<() => void> = [];

  constructor(
    private readonly opts: BoardSyncOptions,
    private readonly onState: (state: BoardSyncState) => void,
  ) {
    activeClients.add(this);
    // Keep the store and pending edits while a laptop is offline/asleep. A
    // failed background connection resumes when the tab can use it again.
    if (typeof window !== "undefined") {
      const network = window;
      const resume = () => this.open();
      const offline = () => {
        clearTimeout(this.timer);
        clearTimeout(this.connectTimer);
        const ws = this.ws;
        this.ws = null;
        ws?.close(1000, "Browser offline");
        this.setStatus("offline");
      };
      network.addEventListener("online", resume);
      network.addEventListener("offline", offline);
      this.stops.push(() => {
        network.removeEventListener("online", resume);
        network.removeEventListener("offline", offline);
      });
      if (typeof document !== "undefined") {
        const page = document;
        page.addEventListener("visibilitychange", resume);
        this.stops.push(() =>
          page.removeEventListener("visibilitychange", resume),
        );
      }
    }
    this.open();
  }

  /** True once every local edit has been sent and acknowledged by the server. */
  isSettled() {
    return !this.outgoing && !this.pending.length;
  }

  private open() {
    if (
      this.disposed ||
      this.ws ||
      (typeof navigator !== "undefined" && navigator.onLine === false) ||
      (typeof document !== "undefined" && document.visibilityState === "hidden")
    )
      return;
    clearTimeout(this.timer);
    const url = new URL(this.opts.uri);
    url.searchParams.set("sessionId", this.sessionId);
    const ws = new WebSocket(url);
    this.ws = ws;
    // A stalled upgrade or protocol handshake must not block all recovery.
    this.connectTimer = setTimeout(() => {
      if (this.ws === ws) ws.close(1000, "Connection timed out");
    }, 15_000);
    ws.onopen = () =>
      ws.send(
        JSON.stringify({
          type: "connect",
          protocol: SYNC_PROTOCOL,
          schema: this.opts.schema.serialize(),
          lastServerClock: this.serverClock,
        }),
      );
    ws.onmessage = ({ data }) => {
      if (this.ws === ws)
        this.receive(JSON.parse(String(data)) as ServerMessage);
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      clearTimeout(this.connectTimer);
      this.ws = null;
      this.setStatus("offline");
      if (this.disposed) return;
      this.timer = setTimeout(
        () => this.open(),
        Math.min(10_000, 500 * 2 ** this.retry++),
      );
    };
  }

  private setStatus(status: "offline" | "online") {
    this.collaboration.status.set(status);
    if (this.store)
      this.onState({
        status: "synced-remote",
        connectionStatus: status,
        store: this.store,
      });
  }

  private send(message: object) {
    if (this.ws?.readyState === WebSocket.OPEN)
      this.ws.send(JSON.stringify(message));
  }

  private receive(message: ServerMessage) {
    switch (message.type) {
      case "connect": {
        clearTimeout(this.connectTimer);
        this.retry = 0;
        this.collaboration.mode.set(
          message.isReadonly ? "readonly" : "readwrite",
        );
        if (!this.store) this.start(message.diff);
        else {
          this.flush();
          this.rebase(message.diff, message.hydrate);
          // The server may not have seen these; puts and patches are idempotent.
          for (const p of this.pending)
            this.send({
              type: "push",
              clientClock: p.clientClock,
              diff: p.ops,
            });
        }
        this.serverClock = message.serverClock;
        this.setStatus("online");
        this.sendPresence();
        break;
      }
      case "patch":
        this.flush();
        this.rebase(message.diff);
        this.serverClock = message.serverClock;
        break;
      case "push_result": {
        const index = this.pending.findIndex(
          (p) => p.clientClock === message.clientClock,
        );
        if (index < 0) break;
        const [done] = this.pending.splice(index, 1);
        this.flush();
        if (message.action === "commit")
          for (const [id, op] of Object.entries(done.ops)) {
            if (!this.server.has(id)) continue;
            try {
              const base = this.server.get(id) ?? undefined;
              this.server.set(id, applyRecordOp(base, op) as TLRecord | null);
            } catch {
              // The server accepted it, so a later patch will carry its state.
            }
          }
        this.rebase(
          message.action === "rebase" ? (message.diff ?? {}) : {},
          false,
          message.action === "rebase",
        );
        this.serverClock = Math.max(this.serverClock, message.serverClock);
        this.forgetSettled();
        break;
      }
      case "presence":
        this.store?.mergeRemoteChanges(() => this.applyRemote(message.diff));
        break;
      case "custom":
        this.opts.onCustomMessage?.(message.data);
        break;
      case "incompatible":
        this.dispose();
        this.onState({
          status: "error",
          error: new Error(
            message.reason === "client-outdated"
              ? "A new version of the app is available. Reload to continue."
              : "This board needs a newer version of the app.",
          ),
        });
        break;
    }
  }

  private start(diff: RecordsDiffMessage) {
    const store = this.opts.createStore(this.collaboration);
    this.store = store;
    store.mergeRemoteChanges(() => this.applyRemote(diff));
    this.stops.push(
      store.listen(
        ({ changes }) => {
          this.outgoing = this.outgoing
            ? squashRecordDiffs([this.outgoing, changes])
            : changes;
          queueMicrotask(() => this.flush());
        },
        { source: "user", scope: "document" },
      ),
    );
    if (this.opts.user) {
      const presence = createPresenceStateDerivation(this.opts.user)(store);
      this.stops.push(
        react("sync presence", () => {
          presence.get();
          this.schedulePresence(presence);
        }),
      );
      this.presence = presence;
    }
  }

  private presence?: Signal<unknown>;
  private schedulePresence(presence: Signal<unknown>) {
    if (this.presenceTimer) return;
    this.presenceTimer = setTimeout(() => {
      this.presenceTimer = undefined;
      this.send({ type: "push", clientClock: -1, presence: presence.get() });
    }, PRESENCE_MS);
  }
  private sendPresence() {
    if (this.presence) this.schedulePresence(this.presence);
  }

  /** Turn batched local edits into a pending push. */
  private flush() {
    const diff = this.outgoing;
    if (!diff) return;
    this.outgoing = null;
    const ops: RecordsDiffMessage = {};
    const track = (id: string, before: TLRecord | null) => {
      if (!this.server.has(id)) this.server.set(id, before);
    };
    for (const [id, record] of Object.entries(diff.added)) {
      track(id, null);
      ops[id] = ["put", record as unknown as Record<string, unknown>];
    }
    for (const [id, [from, to]] of Object.entries(diff.updated)) {
      track(id, from);
      ops[id] = [
        "patch",
        diffFields(
          from as unknown as Record<string, unknown>,
          to as unknown as Record<string, unknown>,
        ),
      ];
    }
    for (const [id, record] of Object.entries(diff.removed)) {
      track(id, record);
      ops[id] = ["remove"];
    }
    // The server refuses messages over 1 MB; split large batches (single
    // records are capped well below this).
    let chunk: RecordsDiffMessage = {};
    let bytes = 0;
    const push = () => {
      if (!bytes) return;
      const clientClock = this.clientClock++;
      this.pending.push({ clientClock, ops: chunk });
      this.send({ type: "push", clientClock, diff: chunk });
      chunk = {};
      bytes = 0;
    };
    for (const [id, op] of Object.entries(ops)) {
      const size = JSON.stringify(op).length + id.length;
      if (bytes && bytes + size > PUSH_BYTES) push();
      chunk[id] = op;
      bytes += size;
    }
    push();
  }

  private applyRemote(diff: RecordsDiffMessage) {
    const store = this.store!;
    for (const [id, op] of Object.entries(diff)) {
      const current = store.get(id as TLRecord["id"]);
      const next = applyRecordOp(current, op) as TLRecord | null;
      if (next) store.put([next]);
      else if (current) store.remove([current.id]);
    }
  }

  /**
   * Put the store back to the server's state for locally edited records, apply
   * the server's changes, then replay the unacknowledged local edits.
   */
  private rebase(
    remote: RecordsDiffMessage,
    hydrate = false,
    correction = false,
  ) {
    const store = this.store;
    if (!store) return;
    reconcileStoreHistory(
      store,
      () => {
        let canonical: RecordsDiff<TLRecord> | null = null;
        store.mergeRemoteChanges(() => {
          for (const [id, record] of this.server) {
            const current = store.get(id as TLRecord["id"]);
            if (record) store.put([record]);
            else if (current) store.remove([current.id]);
          }
          canonical = store.extractingChanges(() => {
            this.applyRemote(remote);
            for (const [id, op] of Object.entries(remote))
              if (this.server.has(id))
                this.server.set(
                  id,
                  op[0] === "remove"
                    ? null
                    : (store.get(id as TLRecord["id"]) ?? null),
                );
            if (hydrate) {
              const scope = this.opts.schema.types as Record<
                string,
                { scope: string }
              >;
              const stale = store
                .allRecords()
                .filter(
                  (r) =>
                    scope[r.typeName]?.scope === "document" &&
                    !(r.id in remote),
                )
                .map((r) => r.id);
              store.remove(stale);
              for (const id of this.server.keys())
                if (!(id in remote)) this.server.set(id, null);
            }
          });
          for (const { ops } of this.pending)
            for (const [id, op] of Object.entries(ops)) {
              try {
                const current = store.get(id as TLRecord["id"]);
                const next = applyRecordOp(current, op) as TLRecord | null;
                if (next) store.put([next]);
                else if (current) store.remove([current.id]);
              } catch {
                // The record was removed remotely; this edit no longer applies.
              }
            }
        });
        // A rejected push may have no canonical patch; its visible correction
        // still invalidates the refused action. Acknowledgements and temporary
        // rollback/replay must never erase independent pending user history.
        if (hydrate && canonical) {
          // A reconnect snapshot can contain our own accepted writes whose ack
          // was lost. Those echoes must not supersede the corresponding history.
          const incoming = canonical as RecordsDiff<TLRecord>;
          for (const id of new Set(
            this.pending.flatMap((p) => Object.keys(p.ops)),
          )) {
            const pair = incoming.updated[id as TLRecord["id"]];
            const added = incoming.added[id as TLRecord["id"]];
            let expected: TLRecord | null = pair?.[0] ?? null;
            let independent = pair?.[1];
            for (const pending of this.pending) {
              const op = pending.ops[id];
              if (!op) continue;
              try {
                expected = applyRecordOp(
                  expected ?? undefined,
                  op,
                ) as TLRecord | null;
              } catch {
                continue;
              }
              if (added && isEqual(JSON.parse(JSON.stringify(expected)), added))
                delete incoming.added[added.id];
              if (pair && expected && independent)
                independent = mergeFields(independent, expected, pair[0], true);
            }
            if (pair && independent) {
              if (isEqual(pair[0], independent))
                delete incoming.updated[pair[0].id];
              else incoming.updated[pair[0].id] = [pair[0], independent];
            }
          }
        }
        return Object.keys(remote).length || hydrate ? canonical : null;
      },
      correction,
    );
  }

  private forgetSettled() {
    const live = new Set(this.pending.flatMap((p) => Object.keys(p.ops)));
    for (const id of this.server.keys())
      if (!live.has(id)) this.server.delete(id);
  }

  dispose() {
    this.disposed = true;
    activeClients.delete(this);
    clearTimeout(this.timer);
    clearTimeout(this.connectTimer);
    clearTimeout(this.presenceTimer);
    for (const stop of this.stops) stop();
    const ws = this.ws;
    this.ws = null;
    ws?.close(1000, "Left board");
  }
}

/** React binding: the store's sync status, shaped like the editor expects. */
export function useBoardSync(opts: BoardSyncOptions): BoardSyncState {
  const [state, setState] = useState<BoardSyncState>({ status: "loading" });
  useEffect(() => {
    setState({ status: "loading" });
    const client = new BoardSyncClient(opts, setState);
    return () => client.dispose();
    // The connection is per board URI; other options are read when needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.uri]);
  return state;
}
